/**
 * notificationDsl.js — bidirectional sync between form state and the
 * `mbd.notification(...)` DSL string.
 *
 *   generateDsl(form, { algosById }) -> string
 *   parseDsl(string,  { algosByName }) -> { ok: true, value } | { ok: false, error }
 *
 * Algos can be written as either numeric ids or string names in the DSL:
 *   .algos([125, 126])
 *   .algos(['polymarket-notifications', 'hyperliquid-notifications'])
 *   .algos([125 /* polymarket-notifications *​/])  // inline comments OK
 *
 * Generation annotates each id with the algo's name as a JS block comment so
 * the human reader sees what each id refers to.
 *
 * The parser runs the DSL inside `new Function('mbd', code)` with a stubbed
 * mbd that only records builder calls. No network, no real Studio SDK.
 */

function algoRef(id, algosById) {
  // Prefer the human-readable name. Fall back to the numeric id if the algo
  // isn't in the cheatsheet (e.g. it was deleted but is still referenced).
  const name = algosById?.get?.(Number(id))?.name;
  return name ? JSON.stringify(name) : String(id);
}

function renderAlgoList(algoIds, algosById) {
  if (!algoIds || algoIds.length === 0) return '';
  return algoIds.map((id) => algoRef(id, algosById)).join(', ');
}

export function generateDsl(form, { algosById } = {}) {
  const name = form?.name || 'my-notification';
  const lines = [`mbd.notification(${JSON.stringify(name)})`];

  if (Array.isArray(form?.algo_ids) && form.algo_ids.length > 0) {
    lines.push(`  .algos([${renderAlgoList(form.algo_ids, algosById)}])`);
  }

  if (form?.webhook_url || form?.auth_header) {
    const opts = {};
    if (form.auth_header) opts.authHeader = form.auth_header;
    const optsStr = Object.keys(opts).length > 0 ? `, ${JSON.stringify(opts)}` : '';
    lines.push(`  .webhook(${JSON.stringify(form?.webhook_url || '')}${optsStr})`);
  }

  const hasBudget = form?.daily_budget != null && form.daily_budget !== '';
  const hasPerType = form?.per_type_limits && Object.keys(form.per_type_limits).length > 0;
  if (hasBudget || hasPerType) {
    const parts = [];
    if (hasBudget) parts.push(`daily: ${Number(form.daily_budget)}`);
    if (hasPerType) parts.push(`perType: ${JSON.stringify(form.per_type_limits)}`);
    lines.push(`  .budget({ ${parts.join(', ')} })`);
  }

  const hasCooldownH = form?.cooldown_hours != null && form.cooldown_hours !== '';
  if (hasCooldownH || form?.cooldown_by) {
    const parts = [];
    if (hasCooldownH) parts.push(`hours: ${Number(form.cooldown_hours)}`);
    if (form?.cooldown_by) parts.push(`by: ${JSON.stringify(form.cooldown_by)}`);
    lines.push(`  .cooldown({ ${parts.join(', ')} })`);
  }

  if (form?.priority_filter) {
    lines.push(`  .priorityFilter(${JSON.stringify(form.priority_filter)})`);
  }

  lines.push(`  .activate();`);
  return lines.join('\n');
}

export function parseDsl(code, { algosByName } = {}) {
  const value = {
    name: '',
    algo_ids: [],
    webhook_url: '',
    auth_header: '',
    daily_budget: '',
    per_type_limits: null,
    cooldown_hours: '',
    cooldown_by: '',
    priority_filter: '',
  };
  const unresolvedNames = [];

  let touchedNotification = false;
  const stub = {
    algos(ids) {
      if (!Array.isArray(ids)) return stub;
      const resolved = [];
      for (const x of ids) {
        if (typeof x === 'number' && Number.isInteger(x) && x > 0) {
          resolved.push(x);
        } else if (typeof x === 'string') {
          const trimmed = x.trim();
          const numeric = parseInt(trimmed, 10);
          if (!Number.isNaN(numeric) && String(numeric) === trimmed) {
            resolved.push(numeric);
          } else {
            const hit = algosByName?.get?.(trimmed);
            if (hit?.algo_id) {
              resolved.push(Number(hit.algo_id));
            } else {
              unresolvedNames.push(trimmed);
            }
          }
        }
      }
      value.algo_ids = resolved;
      return stub;
    },
    webhook(url, opts) {
      value.webhook_url = typeof url === 'string' ? url : '';
      if (opts && typeof opts === 'object') {
        if (opts.authBearer) value.auth_header = `Bearer ${opts.authBearer}`;
        else if (opts.authHeader) value.auth_header = String(opts.authHeader);
      }
      return stub;
    },
    budget(opts) {
      if (opts && typeof opts === 'object') {
        if (opts.daily != null) value.daily_budget = Number(opts.daily);
        if (opts.perType && typeof opts.perType === 'object') {
          value.per_type_limits = opts.perType;
        }
      }
      return stub;
    },
    cooldown(opts) {
      if (opts && typeof opts === 'object') {
        if (opts.hours != null) value.cooldown_hours = Number(opts.hours);
        if (opts.by) value.cooldown_by = String(opts.by);
      }
      return stub;
    },
    priorityFilter(v) {
      value.priority_filter = String(v);
      return stub;
    },
    // No-ops accepted so users can include them in their DSL:
    activate() { return stub; },
    save() { return stub; },
    update() { return stub; },
    pause() { return stub; },
    preview() { return stub; },
    test() { return stub; },
    describe() { return stub; },
  };

  const mbd = {
    notification(name) {
      touchedNotification = true;
      value.name = typeof name === 'string' ? name : '';
      return stub;
    },
  };

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('mbd', code);
    fn(mbd);
  } catch (e) {
    return { ok: false, error: e?.message ?? String(e) };
  }

  if (!touchedNotification) {
    return { ok: false, error: 'no mbd.notification(...) call found' };
  }
  if (unresolvedNames.length > 0) {
    return {
      ok: false,
      error: `algo name(s) not found in your saved algos: ${unresolvedNames.join(', ')}`,
    };
  }
  return { ok: true, value };
}
